/**
 * RiskAssessments — structured risk register for service users
 *
 * Legal basis:
 *   - Health & Safety at Work Act 1974 s.2 & s.3
 *   - Management of Health & Safety at Work Regulations 1999, reg.3
 *   - CQC Regulation 12(2)(d) — Safe care and treatment
 *   - NICE CG161 — Falls in older people
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle, Plus, ChevronDown, ChevronUp, Edit3,
  RefreshCw, Archive, CheckCircle2, Clock, Eye, Shield,
  TriangleAlert, Info, CalendarClock,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const RISK_TYPES = [
  { value: 'falls',               label: 'Falls Risk' },
  { value: 'moving_handling',     label: 'Moving & Handling' },
  { value: 'medication',          label: 'Medication Risk' },
  { value: 'pressure_ulcer',      label: 'Pressure Ulcer Prevention' },
  { value: 'environmental',       label: 'Environmental / Home Safety' },
  { value: 'nutrition',           label: 'Nutrition & Hydration' },
  { value: 'infection_control',   label: 'Infection Control' },
  { value: 'mental_capacity',     label: 'Mental Capacity / DoLS' },
  { value: 'wandering_elopement', label: 'Wandering / Elopement' },
  { value: 'financial',           label: 'Financial / Exploitation' },
  { value: 'lone_working',        label: 'Lone Working' },
  { value: 'general',             label: 'General Risk Assessment' },
];

const LIKELIHOOD_OPTIONS = [
  { value: 1, label: 'Rare',            desc: 'May only occur in exceptional circumstances' },
  { value: 2, label: 'Unlikely',        desc: 'Could occur at some time but not expected' },
  { value: 3, label: 'Possible',        desc: 'Might occur at some time' },
  { value: 4, label: 'Likely',          desc: 'Will probably occur in most circumstances' },
  { value: 5, label: 'Almost Certain',  desc: 'Expected to occur in most circumstances' },
];

const SEVERITY_OPTIONS = [
  { value: 1, label: 'Insignificant', desc: 'No injury or harm' },
  { value: 2, label: 'Minor',         desc: 'Minor injury, first aid only' },
  { value: 3, label: 'Moderate',      desc: 'Medical treatment required' },
  { value: 4, label: 'Major',         desc: 'Significant injury, hospitalisation' },
  { value: 5, label: 'Catastrophic',  desc: 'Fatality or permanent disability' },
];

const LEVEL_CONFIG = {
  low:       { label: 'Low',       bg: 'bg-[#D8E6D5]',  text: 'text-[#1F8F5F]', border: 'border-[#1F8F5F]/20', dot: '#1F8F5F',  order: 1 },
  medium:    { label: 'Medium',    bg: 'bg-amber-100',  text: 'text-amber-800', border: 'border-amber-200',    dot: '#C78A12',  order: 2 },
  high:      { label: 'High',      bg: 'bg-red-100',    text: 'text-red-700',   border: 'border-red-200',      dot: '#C54B4B',  order: 3 },
  very_high: { label: 'Very High', bg: 'bg-red-800/10', text: 'text-red-900',   border: 'border-red-400',      dot: '#991B1B',  order: 4 },
};

const REVIEW_OUTCOMES = [
  { value: 'no_change',  label: 'No change required' },
  { value: 'updated',    label: 'Assessment updated' },
  { value: 'escalated',  label: 'Escalated to manager' },
  { value: 'archived',   label: 'Risk mitigated — archive' },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────

const riskScore = (l, s) => (l && s) ? l * s : null;
const riskLevel = (score) => {
  if (!score) return null;
  if (score <= 6) return 'low';
  if (score <= 12) return 'medium';
  if (score <= 19) return 'high';
  return 'very_high';
};
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const isOverdue = (d) => d && d < new Date().toISOString().slice(0, 10);
const isDueSoon = (d) => {
  if (!d) return false;
  const in30 = new Date();
  in30.setDate(in30.getDate() + 30);
  return d <= in30.toISOString().slice(0, 10) && d >= new Date().toISOString().slice(0, 10);
};

// ─── RISK MATRIX PREVIEW ─────────────────────────────────────────────────────
function RiskMatrix({ likelihood, severity }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[10px]">
        <thead>
          <tr>
            <th className="p-1 text-left text-slate-400 font-normal w-16">L \ S</th>
            {[1,2,3,4,5].map(s => (
              <th key={s} className="p-1 text-center text-slate-500 font-medium w-10">
                {['Ins','Min','Mod','Maj','Cat'][s-1]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[5,4,3,2,1].map(l => (
            <tr key={l}>
              <td className="p-1 text-slate-500 font-medium text-right pr-2">
                {['R','U','P','L','AC'][l-1]}
              </td>
              {[1,2,3,4,5].map(s => {
                const score = l * s;
                const level = riskLevel(score);
                const cfg = LEVEL_CONFIG[level];
                const isSelected = l === likelihood && s === severity;
                return (
                  <td key={s} className={`p-0.5 text-center`}>
                    <div className={`w-full h-6 rounded flex items-center justify-center font-bold
                      ${cfg.bg} ${cfg.text}
                      ${isSelected ? 'ring-2 ring-offset-1 ring-[#0F5C5E] scale-110 shadow-md z-10 relative' : 'opacity-60'}
                    `}>
                      {score}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex gap-3 mt-2 flex-wrap">
        {Object.entries(LEVEL_CONFIG).map(([k, v]) => (
          <span key={k} className={`flex items-center gap-1 text-[10px] ${v.text}`}>
            <span className={`w-2 h-2 rounded-full inline-block`} style={{ background: v.dot }} />
            {v.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── RISK FORM ───────────────────────────────────────────────────────────────
function RiskForm({ open, onClose, onSaved, serviceUserId, editing }) {
  const blankForm = {
    assessment_type: 'falls',
    title: '',
    description: '',
    hazards: [{ description: '', persons_at_risk: [] }],
    existing_controls: '',
    likelihood: 3,
    severity: 3,
    additional_controls_required: '',
    residual_likelihood: null,
    residual_severity: null,
    responsible_person: '',
    review_date: '',
    notes: '',
  };

  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [showMatrix, setShowMatrix] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        assessment_type: editing.assessment_type || 'falls',
        title: editing.title || '',
        description: editing.description || '',
        hazards: editing.hazards?.length ? editing.hazards : [{ description: '', persons_at_risk: [] }],
        existing_controls: editing.existing_controls || '',
        likelihood: editing.likelihood || 3,
        severity: editing.severity || 3,
        additional_controls_required: editing.additional_controls_required || '',
        residual_likelihood: editing.residual_likelihood || null,
        residual_severity: editing.residual_severity || null,
        responsible_person: editing.responsible_person || '',
        review_date: editing.review_date || '',
        notes: editing.notes || '',
      });
    } else {
      setForm(blankForm);
    }
  }, [open, editing]);

  if (!open) return null;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const token = localStorage.getItem('token') || localStorage.getItem('adminToken');

  const score = riskScore(form.likelihood, form.severity);
  const level = riskLevel(score);
  const levelCfg = level ? LEVEL_CONFIG[level] : null;
  const resScore = riskScore(form.residual_likelihood, form.residual_severity);
  const resLevel = riskLevel(resScore);
  const resLevelCfg = resLevel ? LEVEL_CONFIG[resLevel] : null;

  const updateHazard = (i, k, v) => {
    const updated = [...form.hazards];
    updated[i] = { ...updated[i], [k]: v };
    set('hazards', updated);
  };
  const addHazard = () => set('hazards', [...form.hazards, { description: '', persons_at_risk: [] }]);
  const removeHazard = (i) => set('hazards', form.hazards.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    const validHazards = form.hazards.filter(h => h.description.trim());
    setSaving(true);
    const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
    const payload = { ...form, hazards: validHazards };
    try {
      if (editing) {
        await axios.put(`${API}/service-users/${serviceUserId}/risk-assessments/${editing.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Risk assessment updated');
      } else {
        await axios.post(`${API}/service-users/${serviceUserId}/risk-assessments`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Risk assessment created');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E] focus:border-[#0F5C5E] bg-white';
  const labelCls = 'block text-xs font-semibold text-slate-700 mb-1';

  return (
    <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0F5C5E] to-[#163A70] rounded-t-2xl px-6 py-5 text-white sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-white/70 uppercase tracking-wider">Risk Register</p>
              <h2 className="text-lg font-bold">{editing ? 'Update Risk Assessment' : 'New Risk Assessment'}</h2>
            </div>
            <button onClick={onClose} className="text-white/60 hover:text-white text-xl">×</button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Type & title */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Assessment type *</label>
              <select className={inputCls} value={form.assessment_type} onChange={e => set('assessment_type', e.target.value)}>
                {RISK_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Title *</label>
              <input className={inputCls} value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Staircase falls risk" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Description / context</label>
            <textarea rows={2} className={inputCls} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Brief description of the situation being assessed..." />
          </div>

          {/* Hazards */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={labelCls + ' mb-0'}>Identified hazards</label>
              <button onClick={addHazard} className="text-xs text-[#0F5C5E] hover:underline flex items-center gap-1">
                <Plus className="h-3 w-3" />Add hazard
              </button>
            </div>
            <div className="space-y-2">
              {form.hazards.map((h, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    className={inputCls}
                    value={h.description}
                    onChange={e => updateHazard(i, 'description', e.target.value)}
                    placeholder={`Hazard ${i + 1}, e.g. Wet floor surface at entrance`}
                  />
                  {form.hazards.length > 1 && (
                    <button onClick={() => removeHazard(i)} className="text-red-400 hover:text-red-600 flex-shrink-0">×</button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Existing controls */}
          <div>
            <label className={labelCls}>Existing controls / current measures in place</label>
            <textarea rows={2} className={inputCls} value={form.existing_controls} onChange={e => set('existing_controls', e.target.value)} placeholder="e.g. Non-slip mat fitted, grab rail installed, carer reminded to check floor..." />
          </div>

          {/* Risk Matrix — Inherent */}
          <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-700">Inherent risk (before controls)</p>
              {levelCfg && (
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${levelCfg.bg} ${levelCfg.text} border ${levelCfg.border}`}>
                  {score} — {levelCfg.label}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Likelihood *</label>
                <select className={inputCls} value={form.likelihood} onChange={e => set('likelihood', parseInt(e.target.value))}>
                  {LIKELIHOOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.value} — {o.label}</option>)}
                </select>
                {form.likelihood && <p className="text-xs text-slate-400 mt-0.5">{LIKELIHOOD_OPTIONS.find(o => o.value === form.likelihood)?.desc}</p>}
              </div>
              <div>
                <label className={labelCls}>Severity *</label>
                <select className={inputCls} value={form.severity} onChange={e => set('severity', parseInt(e.target.value))}>
                  {SEVERITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.value} — {o.label}</option>)}
                </select>
                {form.severity && <p className="text-xs text-slate-400 mt-0.5">{SEVERITY_OPTIONS.find(o => o.value === form.severity)?.desc}</p>}
              </div>
            </div>
            <button className="text-xs text-[#0F5C5E] hover:underline" onClick={() => setShowMatrix(m => !m)}>
              {showMatrix ? 'Hide' : 'Show'} risk matrix
            </button>
            {showMatrix && <RiskMatrix likelihood={form.likelihood} severity={form.severity} />}
          </div>

          {/* Additional controls & residual risk */}
          <div>
            <label className={labelCls}>Additional controls required</label>
            <textarea rows={2} className={inputCls} value={form.additional_controls_required} onChange={e => set('additional_controls_required', e.target.value)} placeholder="e.g. Refer to OT for formal assessment, update care plan..." />
          </div>

          <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-700">Residual risk (after all controls applied)</p>
              {resLevelCfg && resScore && (
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${resLevelCfg.bg} ${resLevelCfg.text} border ${resLevelCfg.border}`}>
                  {resScore} — {resLevelCfg.label}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Residual likelihood</label>
                <select className={inputCls} value={form.residual_likelihood || ''} onChange={e => set('residual_likelihood', e.target.value ? parseInt(e.target.value) : null)}>
                  <option value="">— same as inherent —</option>
                  {LIKELIHOOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.value} — {o.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Residual severity</label>
                <select className={inputCls} value={form.residual_severity || ''} onChange={e => set('residual_severity', e.target.value ? parseInt(e.target.value) : null)}>
                  <option value="">— same as inherent —</option>
                  {SEVERITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.value} — {o.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Management */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Responsible person</label>
              <input className={inputCls} value={form.responsible_person} onChange={e => set('responsible_person', e.target.value)} placeholder="e.g. Care Manager" />
            </div>
            <div>
              <label className={labelCls}>Next review date</label>
              <input type="date" className={inputCls} value={form.review_date} onChange={e => set('review_date', e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Additional notes</label>
            <textarea rows={2} className={inputCls} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any additional information..." />
          </div>
        </div>

        <div className="px-6 pb-6 flex gap-3 sticky bottom-0 bg-white border-t border-slate-100 pt-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
            {saving ? 'Saving…' : editing ? 'Update Assessment' : 'Create Assessment'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── REVIEW DIALOG ────────────────────────────────────────────────────────────
function ReviewDialog({ open, assessment, onClose, onSaved, serviceUserId }) {
  const [outcome, setOutcome] = useState('no_change');
  const [notes, setNotes] = useState('');
  const [nextDate, setNextDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setOutcome('no_change'); setNotes(''); setNextDate(''); }
  }, [open, assessment]);

  if (!open || !assessment) return null;

  const token = localStorage.getItem('token') || localStorage.getItem('adminToken');

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/service-users/${serviceUserId}/risk-assessments/${assessment.id}/review`,
        { outcome, notes, next_review_date: nextDate || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Review recorded');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not record review');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-[#0F5C5E] to-[#163A70] rounded-t-2xl px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-white/70 uppercase tracking-wider">Review</p>
              <h2 className="text-lg font-bold leading-tight">{assessment.title}</h2>
            </div>
            <button onClick={onClose} className="text-white/60 hover:text-white text-xl">×</button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-2">Outcome *</label>
            <div className="space-y-2">
              {REVIEW_OUTCOMES.map(o => (
                <label key={o.value} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all
                  ${outcome === o.value ? 'border-[#0F5C5E] bg-[#D9F1F4]/20' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="radio" value={o.value} checked={outcome === o.value} onChange={() => setOutcome(o.value)} className="accent-[#0F5C5E]" />
                  <span className="text-sm font-medium text-slate-800">{o.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Review notes</label>
            <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm resize-none focus:ring-1 focus:ring-[#0F5C5E]"
              placeholder="Document what was reviewed and any observations..." />
          </div>
          {outcome !== 'archived' && (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Next review date</label>
              <input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E]" />
            </div>
          )}
          {outcome === 'archived' && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">This will archive the assessment. It will be hidden from the active register but preserved in records.</p>
            </div>
          )}
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
            {saving ? 'Saving…' : 'Record Review'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── RISK CARD ────────────────────────────────────────────────────────────────
function RiskCard({ ra, onEdit, onReview, onArchive }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = LEVEL_CONFIG[ra.risk_level] || LEVEL_CONFIG.low;
  const resCfg = ra.residual_risk_level ? LEVEL_CONFIG[ra.residual_risk_level] : null;
  const overdue = isOverdue(ra.review_date);
  const dueSoon = !overdue && isDueSoon(ra.review_date);
  const archived = ra.status === 'archived';

  return (
    <div className={`rounded-2xl border shadow-sm overflow-hidden transition-all
      ${archived ? 'border-slate-100 bg-slate-50/50 opacity-60' : `border-l-4 bg-white`}
      ${!archived ? `border-l-[${cfg.dot}]` : ''}
    `}
      style={{ borderLeftColor: archived ? undefined : cfg.dot }}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Risk level indicator */}
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
            <AlertTriangle className={`h-5 w-5 ${cfg.text}`} />
          </div>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{ra.title}</p>
                <p className="text-xs text-slate-500">{ra.assessment_type_label || ra.assessment_type}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                  {ra.risk_score} — {cfg.label}
                </span>
                {resCfg && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${resCfg.bg} ${resCfg.text} ${resCfg.border}`}>
                    Residual: {ra.residual_risk_score} — {resCfg.label}
                  </span>
                )}
              </div>
            </div>

            {/* Review date */}
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              {ra.review_date && (
                <span className={`flex items-center gap-1 text-xs
                  ${overdue ? 'text-red-600 font-semibold' : dueSoon ? 'text-amber-600 font-medium' : 'text-slate-400'}
                `}>
                  <CalendarClock className="h-3 w-3" />
                  Review: {fmt(ra.review_date)}
                  {overdue && ' — OVERDUE'}
                  {dueSoon && ' — Due soon'}
                </span>
              )}
              {ra.responsible_person && (
                <span className="text-xs text-slate-400">{ra.responsible_person}</span>
              )}
              {ra.last_reviewed_at && (
                <span className="text-xs text-slate-400">Last reviewed {fmt(ra.last_reviewed_at)}</span>
              )}
            </div>
          </div>

          {/* Action buttons */}
          {!archived && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => setExpanded(e => !e)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
              </button>
              <button onClick={() => onEdit(ra)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" title="Edit">
                <Edit3 className="h-4 w-4 text-slate-400" />
              </button>
              <button onClick={() => onReview(ra)} className="p-1.5 rounded-lg hover:bg-[#D9F1F4]/50 transition-colors" title="Record review">
                <CheckCircle2 className="h-4 w-4 text-[#0F5C5E]" />
              </button>
              <button onClick={() => onArchive(ra)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Archive">
                <Archive className="h-4 w-4 text-slate-400 hover:text-red-400" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-slate-100 space-y-3">
          {ra.description && (
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-0.5">Description</p>
              <p className="text-xs text-slate-700">{ra.description}</p>
            </div>
          )}
          {ra.hazards?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-1">Identified hazards</p>
              <ul className="space-y-1">
                {ra.hazards.map((h, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-slate-700">
                    <span className="text-slate-400 mt-0.5">•</span>{h.description}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {[
              ['Existing controls', ra.existing_controls],
              ['Additional controls required', ra.additional_controls_required],
              ['Completed by', ra.completed_by_name],
              ['Notes', ra.notes],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k}>
                <p className="text-xs font-semibold text-slate-500">{k}</p>
                <p className="text-xs text-slate-700">{v}</p>
              </div>
            ))}
          </div>
          {/* Likelihood × Severity summary */}
          <div className="flex items-center gap-4">
            <div className="text-xs text-slate-500">
              Likelihood: <strong>{ra.likelihood}</strong> ({ra.likelihood_label})
            </div>
            <div className="text-xs text-slate-500">
              Severity: <strong>{ra.severity}</strong> ({ra.severity_label})
            </div>
          </div>
          {/* Review history */}
          {ra.review_history?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-1">Review history ({ra.review_history.length})</p>
              <div className="space-y-1">
                {ra.review_history.slice(-3).map((r, i) => (
                  <div key={i} className="text-xs text-slate-500 flex items-center gap-2">
                    <span>{new Date(r.reviewed_at).toLocaleDateString('en-GB')}</span>
                    <span className="text-slate-700">{r.reviewed_by_name}</span>
                    <Badge className="text-[10px] bg-slate-100 text-slate-600">{r.outcome?.replace(/_/g, ' ')}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MISSING TYPES ALERT ──────────────────────────────────────────────────────
function MissingTypesAlert({ missingTypes, onAdd }) {
  const [show, setShow] = useState(false);
  if (!missingTypes?.length) return null;
  const HIGH_PRIORITY = ['falls', 'moving_handling', 'medication', 'pressure_ulcer', 'environmental'];
  const highPriorityMissing = missingTypes.filter(t => HIGH_PRIORITY.includes(t));
  if (!highPriorityMissing.length) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
      <div className="flex items-start gap-2.5">
        <TriangleAlert className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-amber-800">Recommended assessments missing</p>
          <p className="text-xs text-amber-700 mt-0.5">CQC Reg 12 expects documented risk assessments for high-risk areas.</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {highPriorityMissing.map(t => {
              const type = RISK_TYPES.find(r => r.value === t);
              return (
                <button
                  key={t}
                  onClick={() => onAdd(t)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-100 text-amber-800 text-xs font-medium hover:bg-amber-200 transition-colors border border-amber-200"
                >
                  <Plus className="h-3 w-3" />{type?.label}
                </button>
              );
            })}
          </div>
        </div>
        <button onClick={() => setShow(s => !s)} className="text-amber-600 text-xs hover:underline flex-shrink-0">
          {show ? 'Less' : `+${missingTypes.length - highPriorityMissing.length} more`}
        </button>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function RiskAssessments({ serviceUserId }) {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'branch_manager'].includes(user?.role);

  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [filterType, setFilterType] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const token = localStorage.getItem('token') || localStorage.getItem('adminToken');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        axios.get(`${API}/service-users/${serviceUserId}/risk-assessments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/service-users/${serviceUserId}/risk-summary`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setRecords(listRes.data || []);
      setSummary(summaryRes.data);
    } catch {
      toast.error('Could not load risk assessments');
    } finally {
      setLoading(false);
    }
  }, [serviceUserId, token]);

  useEffect(() => { load(); }, [load]);

  const handleArchiveConfirm = async () => {
    if (!archiveTarget) return;
    const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API}/service-users/${serviceUserId}/risk-assessments/${archiveTarget.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Risk assessment archived');
      setArchiveTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not archive');
    }
  };

  const openFormForType = (typeValue) => {
    setEditing(null);
    setFormOpen(true);
    // Pre-select the type if triggered from missing types alert
    setTimeout(() => {
      setEditing({ _preselect_type: typeValue });
    }, 0);
  };

  const active = records.filter(r => r.status !== 'archived');
  const archived = records.filter(r => r.status === 'archived');
  const filtered = (showArchived ? records : active).filter(r => !filterType || r.assessment_type === filterType);

  const highRisk = active.filter(r => ['high', 'very_high'].includes(r.risk_level));
  const overdueList = active.filter(r => isOverdue(r.review_date));

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Risk Register
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            CQC Reg 12(2)(d) · Management of Health & Safety at Work Regulations 1999
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
            <RefreshCw className={`h-4 w-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {isAdmin && (
            <Button size="sm" onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
              <Plus className="h-3.5 w-3.5 mr-1" />Add assessment
            </Button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Active assessments', value: summary.total_active,         color: 'text-[#0F5C5E] bg-[#D9F1F4]/40', icon: Shield },
            { label: 'High / Very High',   value: summary.high_or_very_high,    color: 'text-red-700 bg-red-50',           icon: AlertTriangle },
            { label: 'Medium risk',         value: summary.medium,               color: 'text-amber-700 bg-amber-50',       icon: TriangleAlert },
            { label: 'Overdue reviews',     value: summary.overdue_reviews,      color: summary.overdue_reviews > 0 ? 'text-red-700 bg-red-50' : 'text-[#1F8F5F] bg-[#D8E6D5]', icon: CalendarClock },
          ].map(c => (
            <div key={c.label} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${c.color}`}>
                <c.icon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500">{c.label}</p>
                <p className="text-xl font-bold text-slate-900">{c.value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Missing types alert */}
      {summary && isAdmin && (
        <MissingTypesAlert missingTypes={summary.missing_types} onAdd={openFormForType} />
      )}

      {/* Overdue banner */}
      {overdueList.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2.5">
          <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-800">
            <strong>{overdueList.length} assessment{overdueList.length > 1 ? 's' : ''} overdue for review:</strong>{' '}
            {overdueList.map(r => r.title).join(', ')}
          </p>
        </div>
      )}

      {/* Filters */}
      {active.length > 3 && (
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilterType('')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${!filterType ? 'bg-[#0F5C5E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            All ({active.length})
          </button>
          {RISK_TYPES.filter(t => active.some(r => r.assessment_type === t.value)).map(t => (
            <button
              key={t.value}
              onClick={() => setFilterType(filterType === t.value ? '' : t.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filterType === t.value ? 'bg-[#0F5C5E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 rounded-full border-2 border-[#0F5C5E] border-t-transparent animate-spin" />
            <p className="text-xs text-slate-400">Loading risk register…</p>
          </div>
        </div>
      ) : filtered.length === 0 && !showArchived ? (
        <div className="text-center py-14 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
          <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-3">
            <AlertTriangle className="h-7 w-7 text-amber-500" />
          </div>
          <p className="text-sm font-semibold text-slate-700 mb-1">No risk assessments yet</p>
          <p className="text-xs text-slate-500 mb-4 max-w-xs mx-auto">
            CQC Reg 12 requires documented risk assessments for falls, moving & handling, medication, and environmental hazards.
          </p>
          {isAdmin && (
            <Button size="sm" onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
              <Plus className="h-3.5 w-3.5 mr-1" />Add first assessment
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {/* High risk first */}
          {highRisk.length > 0 && !filterType && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-red-600 uppercase tracking-wider px-1 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" />High / Very High risk
              </p>
              {highRisk.map(ra => (
                <RiskCard key={ra.id} ra={ra} onEdit={r => { setEditing(r); setFormOpen(true); }} onReview={setReviewTarget} onArchive={setArchiveTarget} />
              ))}
            </div>
          )}
          {/* Remaining */}
          {filtered.filter(r => !['high','very_high'].includes(r.risk_level) || filterType || showArchived).map(ra => (
            <RiskCard key={ra.id} ra={ra} onEdit={r => { setEditing(r); setFormOpen(true); }} onReview={setReviewTarget} onArchive={setArchiveTarget} />
          ))}
        </div>
      )}

      {/* Archived toggle */}
      {archived.length > 0 && (
        <button onClick={() => setShowArchived(s => !s)} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1.5">
          <Archive className="h-3.5 w-3.5" />
          {showArchived ? 'Hide' : 'Show'} {archived.length} archived assessment{archived.length > 1 ? 's' : ''}
        </button>
      )}

      {/* Add/Edit form */}
      <RiskForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSaved={load}
        serviceUserId={serviceUserId}
        editing={editing && !editing._preselect_type ? editing : null}
      />

      {/* Review dialog */}
      <ReviewDialog
        open={!!reviewTarget}
        assessment={reviewTarget}
        onClose={() => setReviewTarget(null)}
        onSaved={load}
        serviceUserId={serviceUserId}
      />

      {/* Archive confirm */}
      {archiveTarget && (
        <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/40 p-4" onClick={() => setArchiveTarget(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Archive className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Archive assessment?</h3>
                <p className="text-xs text-slate-600 mt-1">{archiveTarget.title}</p>
                <p className="text-xs text-slate-500 mt-1">The record will be preserved but hidden from the active register.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setArchiveTarget(null)} className="flex-1">Cancel</Button>
              <Button onClick={handleArchiveConfirm} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white">Archive</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
